import { Box, Grid } from '@material-ui/core';
import { TreeList } from '@progress/kendo-react-treelist';
import { ScrollMode } from '@progress/kendo-react-treelist/dist/npm/ScrollMode';
import cx from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import {
  cloneDeep,
  concat,
  isEmpty,
  map,
  omit,
  replace,
} from 'lodash';
import { useObserver } from 'mobx-react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SM } from '../../../../../App';
import { translate } from '../../../../../common/intl';
import { companySelectors } from '../../../../../state/ducks/company';
import { LHRBuild } from '../../../../administration/general.settings/panels/LHRBuild/types';
import ScanCaptureDialog from '../../../../components/common/dialogs/scaner';
import { ScanSelectedPartItem } from '../../../../components/common/dialogs/scaner/components/lot.table/types';
import { getTableMinWidth } from '../../../../components/common/treelist/getTableMinWidth';
import { CustomTreeListColumnProps } from '../../../../components/common/treelist/types';
import { Button as AddButton } from '../../../../components/forms/fields-next';
import KendoDataGridShell, { toKendoDataGridShellColumns } from '../../../../components/KendoDataGrid/KendoDataGridShell';
import useDialog from '../../../../hooks/useDialog';
import { FBProcedureItemConfig, FBProcedureItemType } from '../../../FBProcedureItem/FBProcedureItem.types';
import { FB } from '../../../helpers';
import { EditableEquipmentItem, EquipmentItemEditEvent } from '../../types';
import {
  ADD_FIELD,
  DATA_ITEM_KEY,
  EB_FIELDS_TO_OMIT,
  EB_PARENT_FIELDS_TO_OMIT,
  EB_SUB_ITEMS_FIELD,
  EXPAND_FIELD,
  MODE_FIELD, SUB_ITEMS_FIELD,
  TREELIST_CONFIG,
} from './constants';
import { useEquipmentActions } from './hooks/useEquipmentActions';
import Placeholder from './placeholder';
import { TreeListEBSchema } from './schemas/ebSchema';
import { TreeListLHRTSchema } from './schemas/lhrtSchema';
import { TreeListMPISchema } from './schemas/mpiSchema';
import { TreeListSchema } from './schemas/schema';
import { getEquipmentShellColumns } from './shellColumns';
import useStyles from './styles';
import {
  CustomTreeListRowProps,
  EquipmentItemState,
  FBEquipmentProps,
} from './types';
import { createEquipmentAddItem, getMapSelectedByParentEquipId, prepareData } from './utils';
import View from './view';
import { withFBEquipmentTable } from './wrap';

interface EquipmentColumnHandlers {
  getItem?: (id: string) => string
  addActualId: (dataItem: EditableEquipmentItem) => void
  onDiscard: (dataItem?: EditableEquipmentItem) => void
  onDelete: (dataItem: EditableEquipmentItem) => void
  onConfirm: () => void
  onScanClick: (dataItem: EditableEquipmentItem) => void
  onRowClick: (event: EquipmentItemEditEvent) => void
}

interface EquipmentTreelistContextProps {
  isRedlineActive?: boolean
  isEnableActualIdAddition?: boolean
  isEqProdConfigEnforced?: boolean
  isLHRProductionBuild?: boolean
  disabledOptions?: string[]
  lhrBuildTypeConfigForSelectedBuildType?: LHRBuild | undefined
}

export const EquipmentTreelistContext = React.createContext<EquipmentTreelistContextProps>({
  isRedlineActive: false,
  isEnableActualIdAddition: false,
});

const FBEquipmentTableInner: React.FC<FBEquipmentProps> = ({
  items,
  type,
  getItem,
  handleRemove,
  saveActualEquipment,
  saveScannedEquipments,
  handleSelectionDelete,
  isDisabled,
  isPreview,
  isDesign,
  isLHRStep,
  lhrStepIndex,
  isMPIProcedure,
  isPendingChange,
  equipsSelected,
  isEnableActualIdAddition,
  isEBProcedure,
  isDeviationMode,
  hideDeviationChanges,
  baseItems,
  startInAddMode = false,
}) => {
  const { _tabsState } = SM.useStores();
  const { procedureState, mpiProcedureState, workspaceState, lhrStepState, procedureCatalogState } = FB.useStores();
  const treeListRef = useRef(null);
  const scanDialog = useDialog();
  const isLHRProductionBuild = workspaceState?.isLHRProductionBuild || false;
  const subItemsKey = isEBProcedure ? EB_SUB_ITEMS_FIELD : SUB_ITEMS_FIELD;
  const scanSelectedEquipInfo = useRef<ScanSelectedPartItem>();
  const size = useObserver(() => _tabsState?.contentSize);
  const classes = useStyles({ size });
  const isRedlineActive = useSelector(companySelectors.getRedlineActive);
  const disabledOptions = useMemo(() => map(items, 'title'), [items]);
  const lhrBuildTypeConfigForSelectedBuildType = workspaceState?.lhrBuildTypeConfigForSelectedBuildType;

  const [initialAddItem] = useState<EditableEquipmentItem | undefined>(
    () => startInAddMode ? createEquipmentAddItem() : undefined,
  );
  const [state, setState] = useState<EquipmentItemState>(() => ({
    data: initialAddItem ? [initialAddItem] : prepareData(cloneDeep(items) ?? [], isEBProcedure),
    dataState: {
      sort: [],
      filter: [],
    },
    expanded: [],
    inEdit: initialAddItem ? [initialAddItem] : [],
  }));

  useEffect(() => {
    if (!items?.length) {
      return;
    }

    const mapByEquipmentId = getMapSelectedByParentEquipId(equipsSelected ?? [], items);
    const updatedItems = prepareData(cloneDeep(items) ?? [], isEBProcedure);
    const expandedRows = [] as string[];

    Object.keys(mapByEquipmentId)?.map(equipmentId => {
      const index = updatedItems?.findIndex((item) => {
        return item.title === equipmentId;
      });

      if (index !== -1) {
        const id = updatedItems[index]?.id;
        if (!expandedRows.includes(id)) {
          expandedRows.push(id);
        }

        const subItems = state.data[index][subItemsKey];
        if (subItems?.length) {
          mapByEquipmentId[equipmentId]?.forEach((item) => {
            const matchedItem = subItems?.find((entry) => entry.id === item.id);
            if (matchedItem) {
              item.actualId = matchedItem.actualId;
            }
          });
          updatedItems[index][subItemsKey] = mapByEquipmentId[equipmentId];
        } else {
          updatedItems[index][subItemsKey] = mapByEquipmentId[equipmentId];
        }
      }
    });

    setState(prevState => ({
      ...prevState,
      data: [...updatedItems],
      expanded: expandedRows.length ? expandedRows : prevState.expanded,
      inEdit: [],
    }));
  }, [equipsSelected, items]);

  const formik = useFormik<Partial<EditableEquipmentItem>>({
    initialValues: initialAddItem ?? {},
    onSubmit: (values: Partial<EditableEquipmentItem>) => {
      let isEquipmentSpecification = false;
      if (isEnableActualIdAddition && !isEBProcedure && !isDeviationMode) {
        if (values[ADD_FIELD]) {
          saveActualEquipment?.(values as EditableEquipmentItem);
        }
        discardEquipItem(values as EditableEquipmentItem);
        return;
      }

      if (isMPIProcedure) {
        const selectedEquipInfo = procedureCatalogState?.getEquipmentRecord<{ document?: { docId?: string } }>(values.title);
        if (selectedEquipInfo?.document?.docId?.startsWith('ES-')) {
          isEquipmentSpecification = true;
        }
      }

      const { id = '', plannedId = '', title = '', LHRTparameter } = values;
      let newEquipItem: EditableEquipmentItem = {
        id,
        title: (isDesign || isDeviationMode) ? title : plannedId,
        ...(values.type && { type: values.type }),
      };

      if (isEBProcedure && !values[ADD_FIELD] && !isEmpty(values[EB_SUB_ITEMS_FIELD])) {
        newEquipItem[EB_SUB_ITEMS_FIELD] = values?.[EB_SUB_ITEMS_FIELD]?.map((item) => omit(item, EB_FIELDS_TO_OMIT));
      }

      if (!isEBProcedure) {
        newEquipItem.isEquipmentSpecification = isEquipmentSpecification;
      }

      if (isLHRStep && lhrStepIndex) {
        updateLHRTEquipmentItem(values);
        return;
      }

      if (isDesign) {
        newEquipItem.LHRTparameter = LHRTparameter;
      }
      const allSteps = workspaceState?.schema?.filter((item) => item.type === 'mpiprocedure');

      if (isEBProcedure && (values.isActualIdAddition || values.equipmentId)) {
        newEquipItem = {
          id,
          equipmentId: values.equipmentId,
        };
        const parentIndex = state.data?.findIndex((data) => data.id === values.parentId);
        if (parentIndex !== -1) {
          if (values[ADD_FIELD] || values.equipmentId) {
            const subItems = state.data[parentIndex][subItemsKey];

            if (Array.isArray(subItems)) {
              const existingSubIndex = subItems.findIndex((item) => item.id === values.id);

              if (existingSubIndex !== -1) {
                subItems[existingSubIndex] = newEquipItem;
              } else {
                subItems.push(newEquipItem);
              }
              state.data[parentIndex][subItemsKey] = subItems.map(
                (item) => omit(item, EB_FIELDS_TO_OMIT) as unknown as FBProcedureItemConfig,
              );
            } else {
              state.data[parentIndex][subItemsKey] = [newEquipItem];
            }
          }
        }

        const payload = omit(state.data[parentIndex], EB_PARENT_FIELDS_TO_OMIT) as FBProcedureItemConfig;
        discardEquipItem(values as EditableEquipmentItem);
        procedureState?.addItem(payload, type, allSteps);
        mpiProcedureState?.addItem(payload, type, allSteps);
        return;
      }

      if (isEBProcedure) {
        newEquipItem = omit(newEquipItem, EB_FIELDS_TO_OMIT) as FBProcedureItemConfig;
      }

      discardEquipItem(values as EditableEquipmentItem);
      procedureState?.addItem(newEquipItem as FBProcedureItemConfig, type, allSteps);
      mpiProcedureState?.addItem(newEquipItem as FBProcedureItemConfig, type, allSteps);
    },
  });

  const {
    discardEquipItem,
    editEquipmentItem,
    addEquipItem,
    onExpandChange,
    addActualId,
    removeEquipItem,
    processData,
    updateLHRTEquipmentItem,
    isEqProdConfigEnforced,
  } = useEquipmentActions(
    state,
    setState,
    formik,
    {
      isEnableActualIdAddition,
      isEBProcedure,
      isDesign,
      isPreview,
      isLHRStep,
      isMPIProcedure,
      isDisabled,
      isDeviationMode,
      type,
      isPendingChange,
      lhrStepIndex,
      isRedlineActive,
    },
    {
      handleSelectionDelete,
      handleRemove,
      procedureState,
      workspaceState,
      lhrStepState,
    },
  );

  const openScanDialog = useCallback((dataItem: EditableEquipmentItem) => {
    const plannedIdLabel = procedureCatalogState?.getEquipmentRevisionLabel(dataItem.title) ?? '';
    // eslint-disable-next-line
    const match = plannedIdLabel.match(/^(.+?) Rev (.+?) - (.+)$/) ?? ['', '', ''];

    scanSelectedEquipInfo.current = {
      docId: match[1] ?? '',
      partDescription: match[3],
      displayRevision: match[2],
      partRevId: dataItem.title,
      id: dataItem.id,
      plannedId: dataItem.title,
      type: FBProcedureItemType.equipment,
    };
    scanDialog.open();
  }, [procedureCatalogState, scanDialog]);

  const columnHandlersRef = useRef<EquipmentColumnHandlers>({
    getItem,
    addActualId,
    onDiscard: discardEquipItem,
    onDelete: removeEquipItem,
    onConfirm: formik.submitForm,
    onScanClick: openScanDialog,
    onRowClick: editEquipmentItem,
  });
  columnHandlersRef.current = {
    getItem,
    addActualId,
    onDiscard: discardEquipItem,
    onDelete: removeEquipItem,
    onConfirm: formik.submitForm,
    onScanClick: openScanDialog,
    onRowClick: editEquipmentItem,
  };

  const isMPIForm = Boolean(isMPIProcedure && !isLHRStep);
  const columns = useMemo<CustomTreeListColumnProps[]>(() => {
    const schemaBuilder = (() => {
      if (isMPIForm) {
        return TreeListMPISchema;
      }
      if (isLHRStep) {
        return TreeListLHRTSchema;
      }
      if (isEBProcedure) {
        return TreeListEBSchema;
      }
      return TreeListSchema;
    })();

    return schemaBuilder({
      actionsClass: classes.actionsCell,
      popperHolder: classes.popperHolder,
      parentRevId: '',
      isMPIForm,
      isDeviationMode,
      getItem: (id: string) => columnHandlersRef.current.getItem?.(id) ?? '',
      addActualId: (dataItem: EditableEquipmentItem) => columnHandlersRef.current.addActualId?.(dataItem),
      onDiscard: (dataItem?: EditableEquipmentItem) => columnHandlersRef.current.onDiscard?.(dataItem),
      onDelete: (dataItem: EditableEquipmentItem) => columnHandlersRef.current.onDelete?.(dataItem),
      onConfirm: () => {
        columnHandlersRef.current.onConfirm?.();
      },
      onScanClick: (dataItem: EditableEquipmentItem) => columnHandlersRef.current.onScanClick?.(dataItem),
      isDisabled,
      isEnableActualIdAddition,
      isEBProcedure,
      onRowClick: (event: EquipmentItemEditEvent) => columnHandlersRef.current.onRowClick?.(event),
      lhrBuildTypeConfigForSelectedBuildType,
    });
  }, [
    classes.actionsCell,
    classes.popperHolder,
    isDisabled,
    isEBProcedure,
    isDeviationMode,
    isEnableActualIdAddition,
    isLHRStep,
    isMPIForm,
    lhrBuildTypeConfigForSelectedBuildType,
  ]);

  const tableProps = useMemo(() => ({
    style: { tableLayout: 'fixed' as const, minWidth: getTableMinWidth(columns) },
  }), [columns]);

  const rowRender = useCallback((row, props: CustomTreeListRowProps) => {
    const item = props.dataItem as EditableEquipmentItem;
    const parentChildRelkey = item.parentEquipPlannedId ? `equip-row-${item?.actualId}` : `equip-row-${item?.plannedId}`;

    const showDeviationDiff = !hideDeviationChanges && baseItems !== undefined && items?.length && !item.parentEquipPlannedId;
    const baseIds = showDeviationDiff ? new Set((baseItems ?? []).map((i) => i.title)) : null;
    const currentIds = showDeviationDiff ? new Set((items ?? []).map((i) => i.title)) : null;
    const isDeviationRemoved = showDeviationDiff && item.title && baseIds?.has(item.title) && !currentIds?.has(item.title);
    const isDeviationAdded = showDeviationDiff && item.title && !baseIds?.has(item.title) && currentIds?.has(item.title);
    const baseEquipForTitle = (showDeviationDiff && item.title && baseIds?.has(item.title) && currentIds?.has(item.title))
      ? (baseItems ?? []).find((i) => i.title === item.title)
      : null;
    const currentEquipForTitle = baseEquipForTitle ? (items ?? []).find((i) => i.title === item.title) : null;
    const isDeviationChanged = Boolean(baseEquipForTitle && currentEquipForTitle && (
      baseEquipForTitle.quantity !== currentEquipForTitle.quantity
    ));

    const modifiedProps = {
      className: cx(replace(row?.props?.className, 'k-alt', ''), {
        [classes.whiteBg]: isEmpty(item[subItemsKey]),
        [classes.grayBg]: !isEmpty(item[subItemsKey]),
        [classes.errorBg]: !isEmpty(item.type) && isLHRStep && isDesign,
        [classes.deviationRemovedBg]: Boolean(isDeviationRemoved),
        [classes.deviationAddedBg]: Boolean(isDeviationAdded) || isDeviationChanged,
      }),
    };

    const showWithoutActions = !item[MODE_FIELD];

    if (showWithoutActions) {
      return React.cloneElement(row, {
        ...modifiedProps,
        ['data-cy' as string]: `equip-row-${item?.id}`,
        ['data-parent-rel' as string]: parentChildRelkey,
      });
    }

    const editedRow = React.cloneElement(
      row,
      {
        ...modifiedProps,
        ['data-cy' as string]: `equip-row-${item?.id}`,
        ['data-parent-rel' as string]: parentChildRelkey,
        className: cx(row.props.className, classes.updatingRow),
      },
    );

    return <FormikProvider value={formik}>{editedRow}</FormikProvider>;
  }, [classes.errorBg, classes.grayBg, classes.updatingRow, classes.whiteBg, formik, isDesign, isLHRStep, subItemsKey]);

  const cellRender = useCallback((
    tdElement: React.ReactElement<
    HTMLTableCellElement,
    string | React.JSXElementConstructor<any>
    > | null,
  ): React.ReactElement<HTMLTableCellElement> => {
    const props = {
      ...tdElement?.props,
      className: cx(tdElement?.props.className, classes.borderBottom),
    };
    const defaultRendering = { ...tdElement, props: props };
    return defaultRendering as React.ReactElement<HTMLTableCellElement>;
  }, [classes.borderBottom]);

  const treeListData = useMemo(() => processData(), [state]); // eslint-disable-line react-hooks/exhaustive-deps
  const treeListStyle = useMemo(() => ({
    overflow: 'auto' as const,
    maxHeight: size?.height ? `${size.height}px` : '100px',
  }), [size?.height]);
  const contextValue = useMemo(() => ({
    isRedlineActive,
    isEnableActualIdAddition,
    isLHRProductionBuild,
    isEqProdConfigEnforced,
    disabledOptions,
    lhrBuildTypeConfigForSelectedBuildType,
  }), [
    disabledOptions,
    isEnableActualIdAddition,
    isEqProdConfigEnforced,
    isLHRProductionBuild,
    isRedlineActive,
    lhrBuildTypeConfigForSelectedBuildType,
  ]);

  const addScannedEquipments = (scannedEquips) => {
    scanDialog.close();
    const parentIndex = state.data?.findIndex((data) => data.id === scanSelectedEquipInfo.current?.id);
    if (parentIndex === -1) {
      return;
    }

    const updatedScannedEquips = scannedEquips.map((equipment) => {
      return {
        ...equipment,
        parentEquipPlannedId: scanSelectedEquipInfo.current?.plannedId,
      };
    });

    if (isEBProcedure) {
      const newSubItems = scannedEquips.map((equipment) => ({
        id: FB.uniqid,
        equipmentId: equipment.actualId,
      }));

      const parentItem = state.data[parentIndex];
      const existingSubItems = parentItem[subItemsKey] ?? [];
      const updatedSubItems = concat(newSubItems, existingSubItems).map((item) => omit(item, EB_FIELDS_TO_OMIT));
      state.data[parentIndex][subItemsKey] = updatedSubItems;
      const payload = omit(parentItem, EB_PARENT_FIELDS_TO_OMIT) as FBProcedureItemConfig;
      const allSteps = workspaceState?.schema?.filter((item) => item.type === 'mpiprocedure');
      procedureState?.addItem(payload, type, allSteps);
      mpiProcedureState?.addItem(payload, type, allSteps);
      return;
    }

    saveScannedEquipments?.(updatedScannedEquips);
  };

  const isEmptyTable = isEmpty(state.data);

  return (
    <Box className={classes.root} data-cy="lhr-equipment-table">
      <EquipmentTreelistContext.Provider value={contextValue}>
        <Grid item xs={12} ref={treeListRef}>
          {isEmptyTable ? (
            <KendoDataGridShell
              columns={toKendoDataGridShellColumns(columns)}
              withBottomButton={!isDisabled}
              dataCy="equipment-grid-empty"
            />
          ) : (
            <TreeList
              style={treeListStyle}
              {...state.dataState}
              tableProps={tableProps}
              rowHeight={TREELIST_CONFIG.rowHeight}
              scrollable={TREELIST_CONFIG.scrollable as ScrollMode}
              editField={MODE_FIELD}
              expandField={EXPAND_FIELD}
              dataItemKey={DATA_ITEM_KEY}
              subItemsField={subItemsKey}
              className={cx(classes.treeList, 'containing-box-scrollbar', {
                [classes.treeListWithBottomButton]: !isDisabled,
              })}
              onExpandChange={onExpandChange}
              rowRender={rowRender}
              data={treeListData}
              columns={columns}
              onRowClick={editEquipmentItem}
              resizable={TREELIST_CONFIG.resizable}
              reorderable={TREELIST_CONFIG.reorderable}
              sortable={TREELIST_CONFIG.sortable}
              cellRender={cellRender}
            />
          )}
          {(isDeviationMode || !isDisabled) && (
            <AddButton
              fullWidth
              attached
              kind="add"
              className={classes.bottomAddButtom}
              data-cy="add-equipment"
              onClick={addEquipItem}
            >
              {translate('form.builder.add.item')}
            </AddButton>
          )}
        </Grid>
      </EquipmentTreelistContext.Provider>
      {scanDialog.isOpen && (
        <ScanCaptureDialog
          handler={scanDialog}
          onClose={scanDialog.close}
          isSerialNumberHidden
          addScanLotsToPart={addScannedEquipments}
          selectedPart ={scanSelectedEquipInfo.current as ScanSelectedPartItem}
        />
      )}
    </Box>
  );
};

const FBEquipmentTableLoaded = memo(withFBEquipmentTable(FBEquipmentTableInner));

const FBEquipmentTable: React.FC<FBEquipmentProps> = (props) => {
  const {
    items,
    isPreview,
    isDisabled,
    isMPIProcedure,
    isLHRStep,
    isEBProcedure,
    isDeviationMode,
  } = props;
  const isRedlineActive = useSelector(companySelectors.getRedlineActive);
  const [isAdding, setIsAdding] = useState(false);
  const isEmptyItems = isEmpty(items);
  if (!isEmptyItems && isAdding) {
    setIsAdding(false);
  }
  const isViewOnly = Boolean((isPreview || isDisabled) && !isRedlineActive);
  const isMpiForm = Boolean(isMPIProcedure && !isLHRStep);

  // A deviation edits the MPI's equipment (the deviation document itself is always an output/preview)
  if (isViewOnly && isMpiForm && !isDeviationMode) {
    return <View items={items} />;
  }

  if (isEmptyItems && !isAdding) {
    return (
      <Placeholder
        columns={getEquipmentShellColumns({ isMPIProcedure, isLHRStep, isEBProcedure })}
        canAdd={Boolean(isDeviationMode) || !isDisabled}
        onAdd={() => setIsAdding(true)}
      />
    );
  }

  return <FBEquipmentTableLoaded {...props} startInAddMode={isAdding} />;
};

export default memo(FBEquipmentTable);
